/**
 * Pesca IdleMon — espécies estritamente aquáticas + varas.
 * Rotas de combate só usam anfíbios / água de terra.
 */

function normalizeSlug(slug) {
  return String(slug || '').toLowerCase().replace(/_/g, '-');
}

/** Só pesca (nunca em rotas de hunt). */
export const AQUATIC_ONLY_SLUGS = Object.freeze(new Set([
  'magikarp', 'gyarados',
  'horsea', 'seadra', 'kingdra',
  'tentacool', 'tentacruel',
  'chinchou', 'lanturn',
  'carvanha', 'sharpedo',
  'remoraid', 'octillery',
  'feebas', 'milotic',
  'wailmer', 'wailord',
  'barboach', 'whiscash',
  'corphish', 'crawdaunt',
  'luvdisc',
  'finneon', 'lumineon',
  'goldeen', 'seaking',
  'shellder', 'cloyster',
  'staryu', 'starmie',
  'qwilfish', 'corsola',
  'clamperl', 'huntail', 'gorebyss',
  'relicanth',
  'mantyke', 'mantine',
  'seel', 'dewgong',
  'lapras',
]));

export function isAquaticOnly(slug) {
  return AQUATIC_ONLY_SLUGS.has(normalizeSlug(slug));
}

/** Varas: faixa de bandas e intervalo de cast idle. */
export const FISHING_RODS = Object.freeze([
  {
    slug: 'old-rod',
    price: 80,
    minBand: 0,
    maxBand: 3,
    castMs: 5200,
    img: 'public/assets/items/old-rod.svg',
  },
  {
    slug: 'good-rod',
    price: 450,
    minBand: 0,
    maxBand: 7,
    castMs: 3800,
    img: 'public/assets/items/good-rod.svg',
  },
  {
    slug: 'super-rod',
    price: 2200,
    minBand: 0,
    maxBand: 10,
    castMs: 2600,
    img: 'public/assets/items/super-rod.svg',
  },
]);

export const FISHING_ROD_BY_SLUG = Object.freeze(
  Object.fromEntries(FISHING_RODS.map((rod) => [rod.slug, rod])),
);

export function fishingRodFor(slug) {
  return FISHING_ROD_BY_SLUG[normalizeSlug(slug)] || null;
}

export function bestOwnedFishingRod(inventory = {}) {
  for (const rod of [...FISHING_RODS].reverse()) {
    if ((inventory[rod.slug] || 0) > 0) return rod;
  }
  return null;
}

/** Spots da pesca reconstruída (B.4). */
export const FISHING_SPOTS = Object.freeze([
  Object.freeze({ id: 'pier', labelKey: 'fish.spot.pier', minFishLevel: 1, rareBonus: 0 }),
  Object.freeze({ id: 'deep-sea', labelKey: 'fish.spot.deepSea', minFishLevel: 12, rareBonus: 0.12 }),
]);

export const FISHING_BAITS = Object.freeze([
  Object.freeze({ id: 'common-bait', labelKey: 'fish.bait.common', biteBonus: 0.2, rareBonus: 0, craft: Object.freeze({ seed: 2, 'bag-of-pollen': 1 }) }),
  Object.freeze({ id: 'elite-bait', labelKey: 'fish.bait.elite', biteBonus: 0.15, rareBonus: 0.5, craft: Object.freeze({ 'bug-venom': 1, 'giant-ruby': 1 }) }),
]);

/** Materiais de pesca que alimentam o craft. */
export function rollFishingMaterials(spotId = 'pier', fishLevel = 1, random = Math.random, rareBonus = 0) {
  const deep = spotId === 'deep-sea';
  const bonus = Math.max(0, Number(rareBonus) || 0);
  const rolls = [
    { slug: 'fins', chance: deep ? 0.35 : 0.22 },
    { slug: 'water-pendant', chance: deep ? 0.12 : 0.05 },
    { slug: 'pearl', chance: deep ? 0.1 : 0.03 },
    { slug: 'dome-fossil', chance: deep ? 0.02 : 0.005 },
    { slug: 'helix-fossil', chance: deep ? 0.02 : 0.005 },
    { slug: 'old-amber', chance: deep ? 0.01 : 0.002 },
  ];
  const levelBonus = Math.min(0.15, (Number(fishLevel) || 1) * 0.004);
  const out = [];
  for (const row of rolls) {
    if (random() < row.chance + levelBonus + bonus) out.push({ slug: row.slug, qty: 1 });
  }
  return out;
}

export function fishingCastMs(rod, baitId = null, fishLevel = 1) {
  const base = rod?.castMs || 5200;
  const bait = FISHING_BAITS.find((entry) => entry.id === baitId);
  const baitFactor = bait ? 1 - Math.min(0.25, bait.biteBonus * 0.5) : 1;
  const levelFactor = Math.max(0.7, 1 - (Number(fishLevel) || 1) * 0.008);
  return Math.round(base * baitFactor * levelFactor);
}
